import React, { forwardRef } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { noop } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { TypographyBodySize, TypographyColor, TypographyP } from '@bringg-frontend/ui';

import {
	unreadableWorkflowDescription,
	unreadableWorkflowStatusTooltip,
	unreadableWorkflowTitle
} from '../../utils/use-automation-translations';
import UnreadableWorkflow from '../../stores/unreadable-workflow';
import ActiveSwitch from '../workflow-card/components/active-switch/active-switch';
import InfoTags from '../info-tags/info-tags';
import useTeamsTag from '../../hooks/use-teams-tag';
import useScrollToView from '../../hooks/use-scroll-to-view';
import UnreadableWorkflowSummary from './unreadable-workflow-summary';

import styles from './unreadable-workflow-card.module.scss';

const EMPTY_TEAM_IDS: number[] = [];

interface Props {
	shouldScrollToView?: boolean;
	workflow: UnreadableWorkflow;
}

const UnreadableWorkflowFooter = observer(({ workflow }: { workflow: UnreadableWorkflow }) => {
	const teams = useTeamsTag(workflow.team_ids ?? EMPTY_TEAM_IDS);

	return (
		<div className={styles.footer}>
			<InfoTags tags={[teams]} />
			<UnreadableWorkflowSummary updatedByUserId={workflow.updated_by_user_id} updatedAt={workflow.updated_at} />
		</div>
	);
});

const UnreadableWorkflowCard = forwardRef<HTMLDivElement, Props>(function UnreadableWorkflowCard(
	{ shouldScrollToView = false, workflow }: Props,
	ref
) {
	const { t } = useTranslation();
	const scrollToViewRef = useScrollToView(shouldScrollToView);

	return (
		<div
			ref={ref}
			className={classNames(styles.unreadableWorkflowCard, {
				[styles.startAsHighlighted]: shouldScrollToView
			})}
			data-test-id={`workflow-card-${workflow.id}`}
		>
			<div className={styles.header} ref={scrollToViewRef}>
				<div className={styles.title}>
					<BringgIcon
						iconName={BringgFontIcons.Warning}
						className={styles.warningIcon}
						data-test-id="unreadable-workflow-icon"
					/>
					<TypographyP tag="span" size={TypographyBodySize.Medium} data-test-id="unreadable-workflow-title">
						{workflow.title}
					</TypographyP>
					<TypographyP
						tag="span"
						size={TypographyBodySize.Medium}
						color={TypographyColor.Subtle}
						data-test-id="unreadable-workflow-id"
					>
						{t('GLOBAL.ID')} {workflow.id}
					</TypographyP>
				</div>
				<div className={styles.actions}>
					<Tooltip title={t(unreadableWorkflowStatusTooltip)}>
						<span className={styles.switchWrapper} tabIndex={0} data-test-id="unreadable-workflow-status">
							<ActiveSwitch isEnabled={workflow.enabled} isLoading={false} isDisabled onChange={noop} />
						</span>
					</Tooltip>
				</div>
			</div>
			<div className={styles.content}>
				<TypographyP size={TypographyBodySize.Medium} data-test-id="unreadable-workflow-message">
					{t(unreadableWorkflowTitle)}
				</TypographyP>
				<TypographyP
					size={TypographyBodySize.Medium}
					color={TypographyColor.Subtle}
					data-test-id="unreadable-workflow-note"
				>
					{t(unreadableWorkflowDescription)}
				</TypographyP>
			</div>
			<UnreadableWorkflowFooter workflow={workflow} />
		</div>
	);
});

export default observer(UnreadableWorkflowCard);
